// @flow
import React from 'react';
import styles from '../../list-page/ListPage.module.scss';
import './Report.scss';
import moment from 'moment';
import 'moment-duration-format';
import { Table, Tooltip } from 'antd';
import availableIcon from '../../../assets/available-icon.svg';
import checkIcon from '../../../assets/scheduled-check-icon.svg';
import UserPresenter from '../../../presenters/UserPresenter';
import AttendanceEditModal from './AttendanceEditModal';
import axios from 'axios';

export default function MonthlyTable({
  reports = {
    rows: [],
  },
  scheduled,
  notScheduled = 0,
  currentUser,
  onDataChanged,
}: {
  reports?: any,
  scheduled?: number,
  notScheduled?: number,
  currentUser?: any,
  onDataChanged?: () => void,
}) {
  // State for edit modal
  const [editModalVisible, setEditModalVisible] = React.useState(false);
  const [selectedDayData, setSelectedDayData] = React.useState(null);

  // Check if current user is admin
  const currentUserPresenter = currentUser
    ? new UserPresenter(currentUser)
    : null;
  const isAdmin = currentUserPresenter?.isAdminUserType() || false;

  // Handle cell click for editing (admin only)
  // Only editable if participant is actually on the schedule (IsOnSchedule = true)
  const handleCellClick = (participantData, date) => {
    if (
      !isAdmin ||
      !participantData ||
      participantData.HoursServiced === '-----' ||
      !participantData.IsOnSchedule
    ) {
      return;
    }

    setSelectedDayData({
      participant: participantData.Participant,
      date: participantData.ServiceDate, // Use full ServiceDate instead of processed date
      programs: participantData.Programs || [
        {
          program: participantData.Program,
          hours: participantData.HoursServiced,
        },
      ],
      totalHours: participantData.HoursServiced,
    });
    setEditModalVisible(true);
  };

  // Handle modal save
  const handleModalSave = async (saveData) => {
    // TODO: Add audit logging for attendance save completion
    // Should log: user, participant, date, timestamp, action: 'EDIT_ATTENDANCE_SAVED', changes: before/after

    // Call API to save attendance changes
    try {
      await axios.patch(
        `${process.env.REACT_APP_SERVER_URL || ''}/schedules/attendance_edit/`,
        {
          participant: saveData.participant,
          date: saveData.date,
          programs: saveData.programs,
        }
      );

      // Close modal first
      setEditModalVisible(false);
      setSelectedDayData(null);

      // Trigger data refresh if callback provided
      if (onDataChanged) {
        onDataChanged();
      }
    } catch (error) {
      // TODO: Replace with user-friendly error handling
      // Options: toast notification, modal error message, or inline error display
      // Should show: "Failed to save attendance changes. Please try again."
      // Keep modal open on error so user can retry
    }
  };

  // report default global default values for initial computation
  let total = '00:00';
  const scheduledCount = scheduled;
  const notScheduledCount = notScheduled;

  const patchReportData = (rows) => {
    // Determine date for days needed to add
    let numberOfDays = moment(
      rows[0].ServiceDate.slice(0, 7),
      'YYYY-MM'
    ).daysInMonth();

    // Parse participants as an object by keys --> {participant1: [length = daysInMonth], participant2: [length = daysInMonth], ...}
    let participants = {};
    for (let row of rows) {
      if (!participants[row.Participant]) {
        participants[row.Participant] = new Array(numberOfDays);
      }
      let serviceDay = parseInt(row.ServiceDate.slice(8, 10)) - 1;

      if (serviceDay > numberOfDays) {
        continue;
      }

      // Get or initialize the entry for this participant/day
      let dayEntry = participants[row.Participant][serviceDay];

      if (!dayEntry) {
        // Initialize new entry for this participant/day
        participants[row.Participant][serviceDay] = {
          Participant: row.Participant,
          ServiceDate: row.ServiceDate,
          Program: row.Program,
          Programs: [
            {
              program: row.Program,
              hours: row.HoursServiced,
              status: row.Status,
              isOnSchedule: row.IsOnSchedule,
            },
          ],
          HoursServiced: row.HoursServiced,
          Status: row.Status,
          IsOnSchedule: row.IsOnSchedule,
        };
      } else {
        // Add to existing entry - find or create program entry
        let existingProgram = dayEntry.Programs.find(
          (p) => p.program === row.Program
        );

        if (!existingProgram) {
          // New program for this day
          dayEntry.Programs.push({
            program: row.Program,
            hours: row.HoursServiced,
            status: row.Status,
            isOnSchedule: row.IsOnSchedule,
          });
        } else {
          // Same program - sum hours and update status if scheduled
          existingProgram.hours = moment
            .duration(existingProgram.hours)
            .add(row.HoursServiced)
            .format('HH:mm', { trim: false });
          if (row.Status === 'scheduled') {
            existingProgram.status = 'scheduled';
          }
          // Any true wins for isOnSchedule
          if (row.IsOnSchedule) {
            existingProgram.isOnSchedule = true;
          }
        }

        // Recalculate total hours from all programs
        let totalHours = moment.duration('00:00');
        for (let prog of dayEntry.Programs) {
          totalHours = totalHours.add(prog.hours);
        }
        dayEntry.HoursServiced = totalHours.format('HH:mm', { trim: false });

        // Use "any scheduled wins" logic
        dayEntry.Status =
          dayEntry.Status === 'scheduled' || row.Status === 'scheduled'
            ? 'scheduled'
            : 'not_scheduled';

        // Use "any true wins" for IsOnSchedule (editable if any program is scheduled)
        dayEntry.IsOnSchedule = dayEntry.IsOnSchedule || row.IsOnSchedule;
      }
    }

    // Add unserviced days to participants --> {participant1: [{}, {}, {}, ...], participant2: [{}, {}, {}, ...], ...}
    for (let i = 0; i < numberOfDays; i++) {
      for (let participant in participants) {
        if (!participants[participant][i]) {
          let dayCheck = (i + 1).toString();
          if (dayCheck.length === 1) {
            dayCheck = '0' + dayCheck;
          }
          participants[participant][i] = {
            Participant: participant,
            ServiceDate: rows[0].ServiceDate.slice(0, 7) + '/' + dayCheck,
            Program: '',
            HoursServiced: '-----',
            IsOnSchedule: false,
          };
        }
      }
    }

    // Ready data for table UI by merging all participant data and return value
    let patchedRows = [];
    for (let participant in participants) {
      patchedRows = patchedRows.concat(participants[participant]);
    }

    return [patchedRows, numberOfDays];
  };

  const addTimeServiced = (row) => {
    total = moment
      .duration(total)
      .add(row['HoursServiced'])
      .format('H:m', { trim: false });
  };

  // Helper function to get status icon - simplified to 2 statuses
  // Shows green checkmark if participant is on the schedule (IsOnSchedule = true)
  // Shows dotted lines if not on schedule
  const getStatusIcon = (isOnSchedule, size = '18') => {
    if (isOnSchedule) {
      return <img src={checkIcon} alt="scheduled" width={size} height={size} />;
    }
    // Default to not_scheduled (dotted lines) for any other status
    return (
      <img src={availableIcon} alt="not scheduled" width={size} height={size} />
    );
  };

  const formatRenderUITime = (
    time,
    totalRender,
    participantData = null,
    date = null
  ) => {
    // Handle unserviced days (no data for this day)
    if (time === '-----') {
      return (
        <img src={availableIcon} alt="not scheduled" width="18" height="18" />
      );
    }

    // Format time for display
    let formatTime = moment.duration('00:00').add(time).format('H:m');
    formatTime = formatTime.replace(':', 'h ') + 'm';
    if (formatTime.includes(' 0m') && formatTime.length > 2) {
      formatTime = formatTime.substring(0, formatTime.length - 2);
    }

    // For total column, just return formatted time
    if (totalRender) {
      return formatTime;
    }

    // Get the status icon - shows checkmark if participant is on schedule, dotted lines otherwise
    const statusIcon = getStatusIcon(participantData?.IsOnSchedule);

    // Build tooltip content with program names and hours only (no status icons per program)
    // Only show tooltip if participant is actually on the schedule
    if (
      (participantData?.Programs || participantData?.Program) &&
      participantData?.IsOnSchedule
    ) {
      const programs = participantData.Programs || [
        {
          program: participantData.Program,
          hours: participantData.HoursServiced,
        },
      ];

      const tooltipContent = (
        <div>
          {programs.map((programEntry, index) => {
            const programHours = moment
              .duration('00:00')
              .add(programEntry.hours)
              .format('H:m');
            const formattedProgramHours = programHours.replace(':', 'h ') + 'm';
            const cleanFormattedHours =
              formattedProgramHours.includes(' 0m') &&
              formattedProgramHours.length > 2
                ? formattedProgramHours.substring(
                    0,
                    formattedProgramHours.length - 2
                  )
                : formattedProgramHours;

            return (
              <div key={index}>
                <strong>{programEntry.program}:</strong> {cleanFormattedHours}
                {index < programs.length - 1 && (
                  <div style={{ marginBottom: '4px' }}></div>
                )}
              </div>
            );
          })}
          <div
            style={{
              marginTop: '4px',
              borderTop: '1px solid #f0f0f0',
              paddingTop: '4px',
            }}
          >
            <strong>Total:</strong> {formatTime}
          </div>
          {isAdmin && participantData?.IsOnSchedule && (
            <div
              style={{
                marginTop: '4px',
                fontSize: '12px',
                color: '#fff',
                fontStyle: 'italic',
              }}
            >
              Click to edit
            </div>
          )}
        </div>
      );

      // Only cells where participant is on schedule are clickable for admins
      const isClickable = isAdmin && participantData?.IsOnSchedule;
      const clickableIcon = isClickable ? (
        <span
          onClick={() => handleCellClick(participantData, date)}
          style={{ cursor: 'pointer' }}
        >
          {statusIcon}
        </span>
      ) : (
        statusIcon
      );

      return (
        <Tooltip title={tooltipContent} placement="top">
          {clickableIcon}
        </Tooltip>
      );
    }

    return statusIcon;
  };

  const renderReportLogic = (reports) => {
    let rows = reports.rows;
    if (!rows || rows?.length === 0) {
      return '';
    }

    // Patch rows data to include days that were not serviced by the participant
    const rowsLogicInfo = patchReportData(rows);
    rows = rowsLogicInfo[0];
    const numberOfDaysColumn = rowsLogicInfo[1];
    let participantColumnCounter = 1;

    let table_data = {};
    let table_columns = {
      name: {
        title: 'Participant',
        dataIndex: 'Participant',
        key: 'Participant',
        fixed: 'left',
        width: 180,
      },
    };
    for (let i = 0; i < rows.length; i++) {
      let row = rows[i];
      // ServiceDate is in the `YYYY/MM/dd` format and we only want the day
      let dayOffset = 7;
      let date = row['ServiceDate'].slice(dayOffset);
      date = date.replace('/', ' ');

      let name = row['Participant'];

      if (table_data[name]) {
        let renderServiceDayTime = (
          <img src={availableIcon} alt="dashes" width="18" height="18" />
        );
        if (row['HoursServiced'] !== '-----') {
          renderServiceDayTime = formatRenderUITime(
            row['HoursServiced'],
            false,
            row,
            date
          );
        }
        table_data[name][date] = renderServiceDayTime;
        addTimeServiced(row);
        table_data[name]['total'] = formatRenderUITime(total, true);
      } else {
        if (row['HoursServiced'] !== '00:00') {
          addTimeServiced(row);
        }
        table_data[name] = {
          key: i,
          total: formatRenderUITime(total, true),
          [date]: formatRenderUITime(row['HoursServiced'], false, row, date),
          Participant: name,
        };
      }

      table_columns[date] = {
        title: date,
        dataIndex: date,
        width: 60,
        key: date,
      };

      // Reset total serviced time sum for new participant
      participantColumnCounter++;
      if (participantColumnCounter - 1 === numberOfDaysColumn) {
        total = '00:00';
        participantColumnCounter = 1;
      }
    }
    // $FlowFixMe[incompatible-type]
    table_columns['total'] = {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      fixed: 'right',
      width: 100,
    };

    return (
      <Table
        dataSource={Object.values(table_data)}
        columns={Object.values(table_columns)}
        scroll={{ x: '100vw' }}
        label="attendance-table"
      />
    );
  };

  return (
    <div className={styles.main}>
      <div className={styles.reportHeaderLegend}>
        <div className={styles.centerLegend}>
          <div>
            <img src={checkIcon} alt="checkmark" width="18" height="18" />
            <p>Scheduled</p>
          </div>
          <div>
            <img src={availableIcon} alt="dashes" width="18" height="18" />
            <p>Not Scheduled</p>
          </div>
        </div>
      </div>
      <div className={styles.reportHeaderLegend}>
        <div className={styles.centerLegendCount}>
          <div>
            <img src={checkIcon} alt="checkmark" width="18" height="18" />
            <p>{scheduledCount}</p>
          </div>
          <div>
            <img src={availableIcon} alt="dashes" width="18" height="18" />
            <p>{notScheduledCount}</p>
          </div>
        </div>
      </div>
      <div name="attendance-report" className="report-container">
        {reports.length === 0 ? '' : renderReportLogic(reports)}
      </div>

      <AttendanceEditModal
        visible={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedDayData(null);
        }}
        onSave={handleModalSave}
        selectedDayData={selectedDayData}
        currentUser={currentUser}
      />
    </div>
  );
}
